import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { CredentialVerification } from './../models/products/credential-verification';
import { Exam } from './../models/products/exam';
import { ExamAddOn } from './../models/products/exam-addon';
import { OtherProduct } from './../models/products/other-product';
import { IProduct } from './../models/products/product';
import { Renewal } from './../models/products/renewal';
import { IPurchase, Purchase } from './../models/purchase';

import { UserService } from './../../../shared/services/user.service';
import { Certification } from '../models/products/certification';
import { PayNowUser } from './../models/payments/pay-now-user';
import { filter } from 'rxjs/operators';

@Injectable()
export class PurchaseStateService {
  private tempProduct: IProduct;
  private _purchase: IPurchase;
  private _completedPurchase: IPurchase;
  private currentRouteUrl: string;
  private _payNowUser: PayNowUser;
  public previousRouteUrl: string;

  public purchase$ = new BehaviorSubject<IPurchase>(null);

  constructor(private router: Router, private userService: UserService) {
    this.init();
    this.router.events.pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((route: NavigationEnd) => {
        this.previousRouteUrl = this.currentRouteUrl;
        this.currentRouteUrl = route.url;
      });
  }

  get currentState(): IPurchase {
    return this._purchase;
  }

  getPersonForPayNow(): PayNowUser {
    return this._payNowUser;
  }

  SetPersonForPayNow(user: PayNowUser) {
    this._payNowUser = user;
  }

  get isEmpty(): boolean {
    return this._purchase?.products.length === 0;
  }

  get currentItemCount(): number {
    return this._purchase?.itemCount ?? 0;
  }

  getCompletedPurchase(): IPurchase {
    return this._completedPurchase;
  }

  setCompletedPurchase(purchase: IPurchase) {
    this._completedPurchase = purchase;
  }

  setTempProductData(product: IProduct) {
    this.tempProduct = product;
  }

  getTempProductData(): IProduct {
    return this.tempProduct;
  }

  setPractitionerName(name: string) {
    this._purchase.practitionerName = name;
    this.purchase$.next(this._purchase);
  }

  getPractitionerName(): string {
    return this._purchase?.practitionerName;
  }

  setPaymentMethod(pt: string) {
    this._purchase.paymentMethod = pt;
    this.purchase$.next(this._purchase);
  }

  getEffectiveProductPrice(product: IProduct): number {
    if (!product || !product.constructor) {return 0;}
    return this._purchase?.getEffectiveProductPrice(product) ?? 0;
  }

  getBaseProductPrice(product: IProduct): number {
    if (!product || !product.constructor) {return 0;}
    return this._purchase?.getBaseProductPrice(product) ?? 0;
  }

  reset() {
    this.init();
  }

  patchProducts(product: IProduct) {
    const existingIndex = this.findExistingProductIndex(product);
    this.addOrReplaceProduct(product, existingIndex);
    this.purchase$.next(this._purchase);
  }

  removeProduct(product: IProduct) {
    const index = this._purchase.products.findIndex(p => p === product);
    this._purchase.products.splice(index, 1);
    this.purchase$.next(this._purchase);
  }

  private findExistingProductIndex(product: IProduct): number {
    switch (product.constructor) {
      case CredentialVerification:
        return this._purchase.products.findIndex(p => p.code === product.code &&
          (<CredentialVerification>p).sendTo === (<CredentialVerification>product).sendTo);
      case OtherProduct:
        return this._purchase.products.findIndex(p => p.code === product.code && p.activePrice === product.activePrice &&
          (<OtherProduct>p).initials === (<OtherProduct>product).initials);
      case Renewal:
        return this._purchase.products.findIndex(p => p.code === product.code &&
          (<Renewal>p).renewalYear === (<Renewal>product).renewalYear);
      case ExamAddOn:
        return this._purchase.products.findIndex(p => p.code === product.code);
      default:
        return this._purchase.products.findIndex(p => p.code === product.code);
    }
  }

  private addOrReplaceProduct(product: IProduct, index: number) {
    switch (product.constructor) {
      case CredentialVerification:
      case OtherProduct:
        if (index > -1) {
          const existingQuantity = this._purchase.products[index].quantity;
          product.quantity += existingQuantity;
          this._purchase.products.splice(index, 1, product);
        } else {
          this._purchase.products.push(product);
        }
        this.removeExamProducts();
        break;
      case Renewal:
        if (index > -1) {
          this._purchase.products.splice(index, 1, product);
        } else {
          this._purchase.products.push(product);
        }
        this.removeExamProducts();
        break;
      case Certification:
      case Exam:
      case ExamAddOn:
        this.removeNonExamProducts();
        if (product.constructor === ExamAddOn && index !== -1) {
          break;
        }
        this._purchase.products.push(product);
        break;
      default:
        break;
    }
  }

  private removeExamProducts() {
    this._purchase.products
      .filter(p => p.constructor === Exam || p.constructor === ExamAddOn)
      .forEach(p => {
        const index = this._purchase.products.findIndex(p2 => p2 === p);
        this._purchase.products.splice(index, 1);
      });
  }

  private removeNonExamProducts() {
    this._purchase.products
      .filter(p => p.constructor !== Exam && p.constructor !== ExamAddOn)
      .forEach(p => {
        const index = this._purchase.products.findIndex(p2 => p2 === p);
        this._purchase.products.splice(index, 1);
      });
  }

  private init() {
    this._purchase = new Purchase(this.userService); // Initialize _purchase here
    this._purchase.products = [];
    this.purchase$.next(this._purchase);
  }
}